/// <reference types="vite/client" />

import axios from "axios";
import { logger } from "./logger";
import { generateUUID } from "../utils/uuid";
import type { ApiResponse as ArchApiResponse } from "../types";
import { client as apiClient } from "./api/client.gen";
import { RUNNING_VERSION } from "../utils/appVersion";

// Types and interfaces - Updated to match architecture standards
export interface ApiResponse<T = any> extends ArchApiResponse<T> {
  data?: T;
  error?: string;
  correlationId: string;
}

export interface HealthCheckResponse {
  status: "healthy" | "unhealthy";
  timestamp: string;
  uptime?: number;
}

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

// Get the backend API URL from environment variable
const BACKEND_API_URL: string =
  import.meta.env.VITE_BACKEND_API_URL || "http://localhost:3000";

// Configuration for response data logging
const LOG_RESPONSE_DATA: boolean =
  import.meta.env.VITE_DEBUG === "true" ||
  import.meta.env.MODE === "development";
const MAX_RESPONSE_SIZE_FOR_LOGGING: number = 1000;

apiClient.setConfig({
  baseUrl: BACKEND_API_URL,
  credentials: "include", // Send cookies with requests for WorkOS auth
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
apiClient.interceptors.request.use((config) => {
  const correlationId = generateUUID();
  const startTime = Date.now();

  config.headers.set("X-Correlation-ID", correlationId);
  config.headers.set("X-Request-Start-Time", startTime.toString());
  // Report the UI build SHA so the API can log which frontend version each user
  // runs (power#1359). Reuse the app-wide version source + local sentinel (0.1.0,
  // as in App.tsx) so logs agree with the rest of the app in local/dev.
  config.headers.set("X-Studio1-Client-Version", RUNNING_VERSION || "0.1.0");

  logger.apiRequest(
    config.method?.toUpperCase() || "UNKNOWN",
    config.url || "",
    {
      correlationId,
    },
  );

  return config;
});

// Response interceptor - must be synchronous for hey-api client compatibility
apiClient.interceptors.response.use((response, request) => {
  const correlationId = response.headers.get("X-Correlation-ID") as string;
  const startTime =
    parseInt(response.headers.get("X-Request-Start-Time") as string) ||
    Date.now();
  const duration = Date.now() - startTime;

  // Log basic response info synchronously
  logger.apiResponse(
    request.method.toUpperCase() || "UNKNOWN",
    response.url || "",
    response.status,
    duration,
    {
      correlationId,
    },
  );

  // For detailed logging (response body), clone and process asynchronously
  // This doesn't block the response from being returned
  const contentType = response.headers.get("Content-Type") || "";
  if (
    LOG_RESPONSE_DATA &&
    contentType.includes("application/json") &&
    response.status !== 204
  ) {
    response
      .clone()
      .json()
      .then((responseJson) => {
        const responseSize = JSON.stringify(responseJson).length;

        // Special logging for /services endpoint
        if (response.url?.includes("/services")) {
          logger.debug("📋 /services endpoint response:", {
            url: response.url,
            status: response.status,
            statusText: response.statusText,
            correlationId,
            duration: `${duration}ms`,
            responseSize: `${responseSize} bytes`,
            completeJSON: responseJson,
          });
        }

        // Log response data if not too large
        const responseDataForLog =
          responseSize > MAX_RESPONSE_SIZE_FOR_LOGGING
            ? {
                _truncated: true,
                _originalSize: responseSize,
                _preview:
                  JSON.stringify(responseJson).substring(0, 200) + "...",
              }
            : responseJson;

        logger.debug("Response body", {
          correlationId,
          responseData: responseDataForLog,
          responseSize,
        });
      })
      .catch(() => {
        // Silently ignore JSON parse errors for non-JSON responses
      });
  }

  return response;
});

// Helper function to handle API errors
const handleApiError = (error: unknown, operation: string): never => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`${operation} failed: ${message}`);
  }

  if (error instanceof Error) {
    throw new Error(`${operation} failed: ${error.message}`);
  }

  throw new Error(`${operation} failed: Unknown error occurred`);
};

// todo: either remove or include in then apiSpec; queryService is used

// Service interface for backend API calls
export interface Service {
  id: string;
  name: string;
  description: string;
  status: "active" | "inactive" | "maintenance";
  version: string;
  created_at: string;
  updated_at: string;
}

export interface ApiService {
  healthCheck(): Promise<HealthCheckResponse>;
  getServices(): Promise<Service[]>;
}

export const apiService: ApiService = {
  // Health check endpoint
  healthCheck: async (): Promise<HealthCheckResponse> => {
    try {
      const response = await apiClient.get({ url: "/health" });
      return response.data as any;
    } catch (error) {
      return handleApiError(error, "Health check");
    }
  },

  // // Get services endpoint - demonstrates authenticated API call
  getServices: async (): Promise<Service[]> => {
    try {
      const res = await apiClient.get<Service[]>({ url: "/services" });

      // Log the complete JSON response
      logger.debug("🔍 Complete /services response:", {
        status: res.response.status,
        statusText: res.response.statusText,
        headers: res.response.headers,
        data: res.data,
        fullResponse: res,
      });

      return res.data ?? [];
    } catch (error) {
      logger.error("❌ /services request failed:", error as Error);
      return handleApiError(error, "Get services");
    }
  },
};

export default apiService;
